import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BarChart3, FileSpreadsheet, Sparkles, ArrowRight, Check, CreditCard, TrendingUp, Clock, Leaf, Eye, FlaskConical } from "lucide-react";
import { eur } from "@/lib/format";
import { useDocumentHead } from "@/hooks/useDocumentHead";
import PublicHeader from "@/components/PublicHeader";

const HERO = "/hero-hostelero.jpg";
// Sube esta imagen a /public/mano-app.jpg en tu repo frontend
const MANO_APP = "/mano-app.jpg";

export default function Landing() {
  const { t, i18n } = useTranslation();
  // Mismo criterio ya usado en LanguageSwitcher.jsx:17 y lib/format.js:5.
  const lang = i18n.language?.startsWith("en") ? "en" : "es";
  useDocumentHead({
    title: "Tu Aliado Hostelero · Gestión que se paga sola",
    description: "Tu Aliado Hostelero — Gestión que se paga sola. Escandallo, punto de equilibrio y control de costes para bares y restaurantes independientes.",
    path: "/",
  });

  const features = [
    { key: "recipeCost", icon: <FileSpreadsheet /> },
    { key: "waste",      icon: <BarChart3 /> },
    { key: "pl",         icon: <FileSpreadsheet /> },
    { key: "ai",         icon: <Sparkles /> },
    { key: "cashflow",   icon: <BarChart3 /> },
    { key: "reminders",  icon: <FileSpreadsheet /> },
  ];

  const plans = [
    { id: "monthly", nameKey: "monthlyName", price: 30, periodKey: "perMonth", descKey: "monthlyDesc" },
    { id: "yearly", nameKey: "yearlyName", price: 300, periodKey: "perYear", descKey: "yearlyDesc", recommended: true, save: "−16%" },
  ];

  return (
    <div className="min-h-screen bg-brand-cream text-brand-ink">

      {/* ── Header fijo compartido con Calculadora/Blog ──────────────────── */}
      <PublicHeader />

      <main id="main-content" tabIndex={-1}>
      {/* ── PANTALLA 1 — Hero ──────────────────────────────────────────────── */}
      <section className="relative">

        {/* Móvil: imagen completa con overlay */}
        <div className="relative flex min-h-[92svh] flex-col justify-end overflow-hidden md:hidden">
          <img src={HERO} alt="Hostelero revisando su negocio" width="1400" height="763" className="absolute inset-0 h-full w-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
          <div className="relative px-6 pb-10 pt-24 text-white">
            <span className="label-eyebrow !text-white/60">{t("landing.hero.eyebrowMobile")}</span>
            <h1 className="mt-3 font-heading text-4xl font-black leading-[1.05] tracking-tight">
              {t("landing.hero.titleMobileLine1")}<br />{t("landing.hero.titleMobileLine2")}<br />
              <span className="text-brand-terracotta">{t("landing.hero.titleMobileHighlight")}</span>
            </h1>
            <p className="mt-4 text-base text-white/80 leading-relaxed">
              {t("landing.hero.subtitleMobile")}
            </p>
            <p className="mt-3 text-xs text-white/50">
              {t("landing.hero.priceQuestionMobile")} <span className="text-white/80 font-semibold">{t("landing.hero.priceValue")}</span> {t("landing.hero.priceTrial")}
            </p>
            <div className="mt-6">
              <Link to="/calculadora" className="btn-primary w-full justify-center" data-testid="hero-cta-diagnostico">
                {t("landing.hero.ctaDiagnostic")} <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>

        {/* Desktop: grid clásico */}
        <div className="mx-auto hidden max-w-7xl gap-12 px-6 py-16 md:grid lg:grid-cols-12 lg:gap-8 lg:py-24">
          <div className="lg:col-span-7">
            <span className="label-eyebrow">{t("landing.hero.eyebrowDesktop")}</span>
            {/* No es <h1>: ya existe uno arriba (versión móvil, oculta aquí por CSS pero
                presente en el DOM) — dos <h1> en la misma página confunde a los crawlers. */}
            <p className="mt-4 font-heading text-4xl font-black leading-[1.02] tracking-tighter sm:text-5xl lg:text-6xl">
              {t("landing.hero.titleDesktopLine1")}
              <br />
              <span className="text-brand-ink">{t("landing.hero.titleDesktopHighlight")}</span>
            </p>
            <p className="mt-6 max-w-xl text-lg text-brand-stone">
              {t("landing.hero.subtitleDesktop")}
            </p>
            <p className="mt-3 text-sm text-brand-stone/60">
              {t("landing.hero.priceQuestionDesktop")} <span className="font-semibold text-brand-stone">{t("landing.hero.priceValue")}</span> {t("landing.hero.priceTrial")}
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/calculadora" className="btn-primary" data-testid="hero-cta-diagnostico-desk">
                {t("landing.hero.ctaDiagnostic")} <ArrowRight size={16} />
              </Link>
              <Link to="/registro" className="btn-ghost" data-testid="hero-cta-register-desk">
                {t("landing.hero.ctaStartFree")}
              </Link>
            </div>
            <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-brand-stone">
              <span className="flex items-center gap-2"><Check size={16} className="text-brand-forest" /> {t("landing.hero.trustNoCharge")}</span>
              <span className="flex items-center gap-2"><Check size={16} className="text-brand-forest" /> {t("landing.hero.trustCancelAnytime")}</span>
              <span className="flex items-center gap-2"><Check size={16} className="text-brand-forest" /> {t("landing.hero.trustSecureData")}</span>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative">
              <img src={HERO} alt="Hostelero revisando su negocio" width="1400" height="763" loading="lazy" className="aspect-[4/5] w-full rounded-lg object-cover" />
              <div className="absolute -bottom-6 -left-6 hidden w-64 rounded-lg border border-brand-edge bg-card p-5 shadow-sm md:block">
                <div className="label-eyebrow">{t("landing.hero.resultLabel")}</div>
                <div className="mt-2 font-num text-3xl font-bold text-brand-forest">+8.420 €</div>
                <div className="mt-1 text-xs text-brand-stone">{t("landing.hero.resultChange")}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── PANTALLA 2 — Pequeños cambios, dinero real ─────────────────────── */}
      <section className="mx-auto max-w-7xl px-6 py-16 lg:py-24">
        {/* Bloque de imagen estática responsive (sustituye el HTML anterior de
            esta pantalla). 4 variantes idioma×tema ya en frontend/public/.
            El idioma decide en JS cuál PAR de archivos se referencia (mismo
            criterio que LanguageSwitcher.jsx:17 / lib/format.js:5); el tema
            decide con CSS cuál de ese par es visible (mismo patrón sin JS que
            BrandLogo.jsx: dos <img> hermanos, block dark:hidden / hidden
            dark:block) — nunca se montan los 4 assets a la vez. */}
        <div className="mx-auto max-w-[1100px]">
          <img
            src={`/mockup-pequenos-cambios-${lang}-claro.jpg`}
            alt={t("landing.diff.mockupAlt")}
            width="1737"
            height="905"
            loading="lazy"
            className="block h-auto w-full dark:hidden"
          />
          <img
            src={`/mockup-pequenos-cambios-${lang}-oscuro.jpg`}
            alt={t("landing.diff.mockupAlt")}
            width="1737"
            height="905"
            loading="lazy"
            className="hidden h-auto w-full dark:block"
          />
        </div>
      </section>

      {/* ── PANTALLA 3 — Tu bar en el bolsillo ────────────────────────────── */}
      <section id="features" className="mx-auto max-w-7xl px-6 py-16 lg:py-24">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <div className="order-2 lg:order-1">
            <span className="label-eyebrow">{t("landing.mobile.eyebrow")}</span>
            <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight sm:text-4xl">
              {t("landing.mobile.title")}
            </h2>
            <p className="mt-4 text-brand-stone">
              {t("landing.mobile.subtitle")}
            </p>
            <ul className="mt-6 space-y-3">
              {["bullet1", "bullet2", "bullet3", "bullet4", "bullet5"].map((k) => (
                <li key={k} className="flex items-start gap-3 text-sm">
                  <Check size={16} className="mt-0.5 shrink-0 text-brand-forest" />
                  <span>{t(`landing.mobile.${k}`)}</span>
                </li>
              ))}
            </ul>
            {/* Enlace a la landing pública de descarga (tarea "landing pública de
                descarga de app") — vive en el cuerpo, no en PublicHeader. */}
            <div className="mt-6">
              <Link to="/descargar-app" className="btn-primary w-full lg:w-auto" data-testid="landing-cta-download-app">
                {t("landing.mobile.ctaDownload")} <ArrowRight size={16} />
              </Link>
            </div>
          </div>
          {/* Foto de mano con móvil — sube /public/mano-app.jpg al repo */}
          <div className="order-1 flex items-center justify-center lg:order-2">
            <img
              src={MANO_APP}
              alt="TAH en el móvil"
              width="575"
              height="811"
              loading="lazy"
              className="max-h-[520px] w-auto rounded-2xl object-contain drop-shadow-2xl"
            />
          </div>
        </div>
      </section>

      {/* Features grid — solo desktop */}
      <section className="hidden border-y border-brand-edge bg-card md:block">
        <div className="mx-auto max-w-7xl px-6 py-16">
          <div className="max-w-2xl">
            <span className="label-eyebrow">{t("landing.features.eyebrow")}</span>
            <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight sm:text-4xl">
              {t("landing.features.title")}
            </h2>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {features.map((f, i) => (
              <div key={i} className="card-flat">
                <div className="mb-4 inline-grid h-10 w-10 place-items-center rounded-md bg-brand-cream text-brand-terracotta">
                  {React.cloneElement(f.icon, { size: 20, strokeWidth: 1.7 })}
                </div>
                <h3 className="font-heading text-lg font-bold">{t(`landing.features.${f.key}.title`)}</h3>
                <p className="mt-2 text-sm text-brand-stone">{t(`landing.features.${f.key}.desc`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Asesorías individuales (online y presencial) ───────────────────── */}
      <section id="asesorias" className="border-y border-brand-edge bg-card">
        <div className="mx-auto max-w-7xl px-6 py-16 lg:py-24">
          <div className="mx-auto max-w-2xl text-center">
            <span className="label-eyebrow">{t("landing.consulting.eyebrow")}</span>
            <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight sm:text-4xl">
              {t("landing.consulting.title")}
            </h2>
            <p className="mt-4 text-brand-stone">{t("landing.consulting.subtitle")}</p>
          </div>
          <div className="mx-auto mt-12 grid max-w-4xl gap-6 lg:grid-cols-2">
            {[
              { icon: <Eye />, key: "online", premium: false, bullets: ["onlineBullet1", "onlineBullet2", "onlineBullet3"] },
              { icon: <FlaskConical />, key: "presential", premium: true, bullets: ["presentialBullet1", "presentialBullet2", "presentialBullet3"] },
            ].map((c) => (
              <div key={c.key} className={`card-flat !p-8 ${c.premium ? "ring-2 ring-brand-terracotta" : ""}`} data-testid={`consulting-${c.key}`}>
                <div className="mb-4 inline-grid h-10 w-10 place-items-center rounded-md bg-brand-cream text-brand-terracotta">
                  {React.cloneElement(c.icon, { size: 20, strokeWidth: 1.7 })}
                </div>
                <div className="flex items-center gap-2">
                  <h3 className="font-heading text-xl font-bold">{t(`landing.consulting.${c.key}Title`)}</h3>
                  {c.premium && (
                    <span className="rounded-full bg-brand-terracotta px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-white dark:text-black">
                      {t("landing.consulting.premium")}
                    </span>
                  )}
                </div>
                <p className="mt-2 text-sm text-brand-stone">{t(`landing.consulting.${c.key}Desc`)}</p>
                <ul className="mt-6 space-y-2 text-sm">
                  {c.bullets.map((k) => (
                    <li key={k} className="flex items-start gap-2">
                      <Check size={16} className="mt-0.5 text-brand-forest shrink-0" /> {t(`landing.consulting.${k}`)}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="mt-10 flex justify-center">
            <a
              href="mailto:info@tualiadohostelero.com?subject=Quiero%20informaci%C3%B3n%20sobre%20asesor%C3%ADas"
              className="btn-primary"
              data-testid="consulting-cta"
            >
              {t("landing.consulting.cta")} <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </section>

      {/* ── PANTALLA 4 — Precios honestos ──────────────────────────────────── */}
      <section id="precios" className="mx-auto max-w-7xl px-6 py-16 lg:py-24">
        <div className="mx-auto max-w-2xl text-center">
          <span className="label-eyebrow">{t("landing.pricing.eyebrow")}</span>
          <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight sm:text-4xl">
            {t("landing.pricing.title")}
          </h2>
          <p className="mt-4 text-brand-stone">{t("landing.pricing.subtitle")}</p>
        </div>
        <div className="mx-auto mt-12 grid max-w-4xl gap-6 lg:grid-cols-2">
          {plans.map((p) => (
            <div
              key={p.id}
              className={`card-flat !p-8 min-w-0 ${p.recommended ? "ring-2 ring-brand-terracotta" : ""}`}
              data-testid={`landing-plan-${p.id}`}
            >
              {p.recommended && (
                <span className="mb-4 inline-flex items-center gap-1 rounded-full bg-brand-terracotta px-3 py-1 text-xs font-bold uppercase tracking-wider text-white dark:text-black">
                  {t("landing.pricing.recommended")} · {p.save}
                </span>
              )}
              <h3 className="font-heading text-xl font-bold">{t(`landing.pricing.${p.nameKey}`)}</h3>
              <div className="mt-3 flex items-baseline gap-2">
                <span className="font-num text-5xl font-black">{eur(p.price)}</span>
                <span className="text-brand-stone">/{t(`landing.pricing.${p.periodKey}`)}</span>
              </div>
              <div className="mt-1 text-xs font-semibold uppercase tracking-wider text-brand-stone">
                {t("landing.pricing.taxNote")}
              </div>
              <p className="mt-2 text-sm text-brand-stone">{t(`landing.pricing.${p.descKey}`)}</p>
              <ul className="mt-6 space-y-2 text-sm">
                {["bullet1", "bullet2", "bullet3", "bullet4", "bullet5"].map((k) => (
                  <li key={k} className="flex items-start gap-2">
                    <Check size={16} className="mt-0.5 text-brand-forest shrink-0" /> {t(`landing.pricing.${k}`)}
                  </li>
                ))}
              </ul>
              <Link to="/registro" className="btn-primary mt-8 w-full justify-center" data-testid={`landing-pick-${p.id}`}>
                <CreditCard size={16} /> {t("landing.pricing.cta")}
              </Link>
            </div>
          ))}
        </div>
        <p className="mx-auto mt-6 max-w-2xl text-center text-xs text-brand-stone/60">
          {t("landing.pricing.footnote")}
        </p>
      </section>
      </main>

      <footer className="border-t border-brand-edge bg-card py-10">
        <div className="mx-auto max-w-7xl px-6 text-sm text-brand-stone">
          {t("landing.footer", { year: new Date().getFullYear() })}
        </div>
      </footer>
    </div>
  );
}
